/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 *
 * dashboardRegistry — the operations-hub (Home) composition point. Modules
 * contribute KPIs and "Needs you" signals WITHOUT shared importing them: the
 * composition root (App.jsx) collects each module index.js's `dashboardProvider`
 * via import.meta.glob and registers them here (mirrors moduleCatalog). Home
 * then runs every provider in parallel and merges the results.
 *
 * A provider is an async function returning:
 *   { kpis?:  [{ key, label, display, unit?, sub?, status?, to? }],
 *     signals?:[{ key, severity, title, detail?, to?, actionLabel? }] }
 *   status:   'healthy' | 'warn' | 'critical' | 'neutral'
 *   severity: 'critical' | 'warn' | 'info'
 * Providers must be self-contained and tolerant — a throw is caught by Home and
 * that module simply contributes nothing this load.
 */

let _providers = [];

export function setDashboardProviders(providers) {
  _providers = (providers || []).filter((p) => typeof p === "function");
}

export function getDashboardProviders() {
  return _providers;
}

const SEVERITY_RANK = { critical: 0, warn: 1, info: 2 };

/**
 * Run every registered provider in parallel (failures isolated) and merge into
 * a single { kpis, signals } payload. Signals are ordered most-urgent first.
 */
export async function loadDashboard() {
  const results = await Promise.allSettled(_providers.map((p) => p()));
  const kpis = [];
  const signals = [];
  for (const r of results) {
    if (r.status !== "fulfilled" || !r.value) continue;
    if (Array.isArray(r.value.kpis)) kpis.push(...r.value.kpis);
    if (Array.isArray(r.value.signals)) signals.push(...r.value.signals);
  }
  signals.sort((a, b) => (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3));
  return { kpis, signals };
}
